/**
 * Query File Validators
 *
 * Connector-specific validation logic for YAML query files
 */

import type { ConnectorType } from '@/types/connectors';

export interface ValidationError {
  message: string;
  line?: number;
  severity: 'error' | 'warning';
}

export interface QueryInfo {
  name: string;
  type?: 'gaql' | 'ga4' | 'sql' | 'table';
  columns?: string[];
  hasRequiredFields?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  queries: QueryInfo[];
  errors: ValidationError[];
  queryCount: number;
}

/**
 * Validate Google Ads query structure
 * Expected: Each query must have 'select' and 'from' fields
 */
const validateGoogleAdsQueries = (parsedYaml: any): ValidationResult => {
  const errors: ValidationError[] = [];
  const queries: QueryInfo[] = [];

  if (typeof parsedYaml !== 'object' || parsedYaml === null) {
    return {
      valid: false,
      queries: [],
      errors: [{ message: 'Invalid YAML structure: expected object with query definitions', severity: 'error' }],
      queryCount: 0,
    };
  }

  const queryNames = Object.keys(parsedYaml);

  if (queryNames.length === 0) {
    return {
      valid: false,
      queries: [],
      errors: [{ message: 'No queries found in file', severity: 'error' }],
      queryCount: 0,
    };
  }

  for (const queryName of queryNames) {
    const query = parsedYaml[queryName];

    if (typeof query !== 'object' || query === null) {
      errors.push({
        message: `Query "${queryName}" must be an object with 'select' and 'from' fields`,
        severity: 'error',
      });
      continue;
    }

    const hasSelect = 'select' in query;
    const hasFrom = 'from' in query;
    const hasRequiredFields = hasSelect && hasFrom;

    if (!hasSelect) {
      errors.push({
        message: `Query "${queryName}" is missing required field: 'select'`,
        severity: 'error',
      });
    }

    if (!hasFrom) {
      errors.push({
        message: `Query "${queryName}" is missing required field: 'from'`,
        severity: 'error',
      });
    }

    queries.push({
      name: queryName,
      type: 'gaql',
      hasRequiredFields,
    });
  }

  return {
    valid: errors.length === 0,
    queries,
    errors,
    queryCount: queries.length,
  };
};

/**
 * Validate Google Analytics query structure
 * Expected: Each query must have 'select' and 'from' fields (similar to Google Ads)
 */
const validateGoogleAnalyticsQueries = (parsedYaml: any): ValidationResult => {
  const errors: ValidationError[] = [];
  const queries: QueryInfo[] = [];

  if (typeof parsedYaml !== 'object' || parsedYaml === null) {
    return {
      valid: false,
      queries: [],
      errors: [{ message: 'Invalid YAML structure: expected object with query definitions', severity: 'error' }],
      queryCount: 0,
    };
  }

  const queryNames = Object.keys(parsedYaml);

  if (queryNames.length === 0) {
    return {
      valid: false,
      queries: [],
      errors: [{ message: 'No queries found in file', severity: 'error' }],
      queryCount: 0,
    };
  }

  for (const queryName of queryNames) {
    const query = parsedYaml[queryName];

    if (typeof query !== 'object' || query === null) {
      errors.push({
        message: `Query "${queryName}" must be an object with 'select' and 'from' fields`,
        severity: 'error',
      });
      continue;
    }

    const hasSelect = 'select' in query;
    const hasFrom = 'from' in query;
    const hasRequiredFields = hasSelect && hasFrom;

    if (!hasSelect) {
      errors.push({
        message: `Query "${queryName}" is missing required field: 'select'`,
        severity: 'error',
      });
    }

    if (!hasFrom) {
      errors.push({
        message: `Query "${queryName}" is missing required field: 'from'`,
        severity: 'error',
      });
    }

    queries.push({
      name: queryName,
      type: 'ga4',
      hasRequiredFields,
    });
  }

  return {
    valid: errors.length === 0,
    queries,
    errors,
    queryCount: queries.length,
  };
};

/**
 * Main validation function - routes to connector-specific validators
 */
export const validateQueryFileStructure = (
  parsedYaml: any,
  connectorType: ConnectorType
): ValidationResult => {
  switch (connectorType) {
    case 'google_ads':
      return validateGoogleAdsQueries(parsedYaml);

    case 'google_analytics':
      return validateGoogleAnalyticsQueries(parsedYaml);

    default:
      // Generic validation for unsupported connector types
      return {
        valid: false,
        queries: [],
        errors: [
          {
            message: `Query file validation not yet implemented for connector type: ${connectorType}`,
            severity: 'error',
          },
        ],
        queryCount: 0,
      };
  }
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: ValidationError[]): string[] => {
  return errors.map((error) => {
    if (error.line) {
      return `Line ${error.line}: ${error.message}`;
    }
    return error.message;
  });
};
